import type { FC, FormEvent, ReactNode } from 'react'
import type { PlanOption } from '@/types'
import type { CustomerField, CustomerFieldErrors, CustomerFormValues } from '@/lib/dashboard/customer-form'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { LoaderCircle, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { normalizeCustomerForm, validateCustomerForm } from '@/lib/dashboard/customer-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/dashboard/toast'
import { COUNTRIES, DEFAULT_COUNTRY } from '@/constants/dashboard'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/dialog'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface AddCustomerDialogProps {
    variant?: 'default' | 'sidebar'
    className?: string
}

const FIELD_ORDER: CustomerField[] = ['name', 'email', 'planId', 'country']

const emptyValues = (planId = ''): CustomerFormValues => ({
    name: '',
    email: '',
    planId,
    country: DEFAULT_COUNTRY
})

const PlanLabel: FC<{ plan: PlanOption }> = ({ plan }): ReactNode => {
    return (
        <span className='flex items-center gap-1.5'>
            <span data-mdlfy-cms={`plans:${plan.id}:name`}>{plan.name}</span>
            <span className='text-muted-foreground'>
                <span className='tabular-nums'>{formatCurrency(plan.monthlyPrice)}</span>
                {' per month'}
            </span>
        </span>
    )
}

const FieldError: FC<{ id: string, message?: string }> = ({ id, message }): ReactNode => {
    if (!message) return null

    return (
        <p id={id} className='text-xs font-medium text-danger'>
            {message}
        </p>
    )
}

export const AddCustomerDialog: FC<AddCustomerDialogProps> = ({ variant = 'default', className }): ReactNode => {
    const router = useRouter()
    const { notify } = useToast()

    const [open, setOpen] = useState(false)
    const [plans, setPlans] = useState<PlanOption[] | null>(null)
    const [plansFailed, setPlansFailed] = useState(false)
    const [values, setValues] = useState<CustomerFormValues>(emptyValues())
    const [errors, setErrors] = useState<CustomerFieldErrors>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const fieldRefs = useRef<Partial<Record<CustomerField, HTMLElement | null>>>({})

    const defaultPlanId = (list: PlanOption[] | null): string => list?.[0]?.id ?? ''

    const loadPlans = useCallback(async () => {
        setPlansFailed(false)

        try {
            const response = await fetch('/api/plans')

            if (!response.ok) throw new Error(`Plans request failed with ${response.status}`)

            const data = (await response.json()) as { plans: PlanOption[] }

            setPlans(data.plans)
            setValues(current => (current.planId ? current : { ...current, planId: defaultPlanId(data.plans) }))
        }
        catch {
            setPlansFailed(true)
        }
    }, [])

    useEffect(() => {
        if (open && !plans) void loadPlans()
    }, [open, plans, loadPlans])

    const reset = () => {
        setValues(emptyValues(defaultPlanId(plans)))
        setErrors({})
        setFormError(null)
    }

    const handleOpenChange = (next: boolean) => {
        setOpen(next)

        if (!next) reset()
    }

    const update = (field: CustomerField, value: string) => {
        setValues(current => ({ ...current, [field]: value }))
        setErrors(current => ({ ...current, [field]: undefined }))
    }

    const focusFirstError = (fieldErrors: CustomerFieldErrors) => {
        const first = FIELD_ORDER.find(field => fieldErrors[field])

        if (first) fieldRefs.current[first]?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const normalized = normalizeCustomerForm(values)
        const nextErrors = validateCustomerForm(normalized)

        setErrors(nextErrors)
        setFormError(null)

        if (Object.keys(nextErrors).length > 0) {
            focusFirstError(nextErrors)

            return
        }

        setSubmitting(true)

        try {
            const response = await fetch('/api/customers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(normalized)
            })

            const data = (await response.json().catch(() => ({}))) as { message?: string, errors?: CustomerFieldErrors }

            if (!response.ok) {
                const serverErrors = data.errors ?? {}

                setErrors(serverErrors)
                setFormError(data.message ?? 'The customer could not be saved. Try again in a moment.')
                focusFirstError(serverErrors)

                return
            }

            setOpen(false)
            reset()

            notify({
                tone: 'success',
                title: `${normalized.name} was added`,
                description: 'Their first payment is recorded and your numbers are up to date.'
            })

            await router.replace(router.asPath, undefined, { scroll: false })
        }
        catch {
            setFormError('We could not reach the server. Check your connection and try again.')
        }
        finally {
            setSubmitting(false)
        }
    }

    const planItems = (plans ?? []).map(plan => ({ value: plan.id, label: <PlanLabel plan={plan} /> }))
    const countryItems = COUNTRIES.map(country => ({ value: country, label: country }))

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger
                render={
                    <Button
                        className={cn(
                            'h-9 px-3',
                            variant === 'sidebar' && 'w-full bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 focus-visible:ring-sidebar-ring/60',
                            className
                        )}
                    />
                }
            >
                <Plus aria-hidden='true' className='size-4' />
                Add customer
            </DialogTrigger>

            <DialogContent className='gap-5 p-5 sm:max-w-md'>
                <DialogHeader>
                    <DialogTitle className='text-lg font-semibold'>
                        Add a customer
                    </DialogTitle>

                    <DialogDescription>
                        They start today on the plan you pick, and their first monthly payment is recorded.
                    </DialogDescription>
                </DialogHeader>

                <form noValidate onSubmit={handleSubmit} className='grid gap-4'>
                    <div className='grid gap-1.5'>
                        <Label htmlFor='customer-name'>Company name</Label>

                        <Input
                            id='customer-name'
                            ref={element => { fieldRefs.current.name = element }}
                            value={values.name}
                            onChange={event => update('name', event.target.value)}
                            autoComplete='organization'
                            placeholder='Acme Robotics'
                            aria-invalid={errors.name ? true : undefined}
                            aria-describedby={errors.name ? 'customer-name-error' : undefined}
                            className='h-9'
                        />

                        <FieldError id='customer-name-error' message={errors.name} />
                    </div>

                    <div className='grid gap-1.5'>
                        <Label htmlFor='customer-email'>Billing email</Label>

                        <Input
                            id='customer-email'
                            ref={element => { fieldRefs.current.email = element }}
                            type='email'
                            inputMode='email'
                            value={values.email}
                            onChange={event => update('email', event.target.value)}
                            autoComplete='email'
                            placeholder='billing@acmerobotics.com'
                            aria-invalid={errors.email ? true : undefined}
                            aria-describedby={errors.email ? 'customer-email-error' : undefined}
                            className='h-9'
                        />

                        <FieldError id='customer-email-error' message={errors.email} />
                    </div>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <div className='grid gap-1.5'>
                            <Label id='customer-plan-label' htmlFor='customer-plan'>Plan</Label>

                            <Select
                                items={planItems}
                                value={values.planId || null}
                                onValueChange={value => update('planId', typeof value === 'string' ? value : '')}
                                disabled={!plans}
                            >
                                <SelectTrigger
                                    id='customer-plan'
                                    ref={element => { fieldRefs.current.planId = element }}
                                    aria-labelledby='customer-plan-label'
                                    aria-invalid={errors.planId ? true : undefined}
                                    aria-describedby={errors.planId ? 'customer-plan-error' : undefined}
                                    className='h-9 w-full'
                                >
                                    <SelectValue placeholder={plansFailed ? 'Plans unavailable' : 'Loading plans'} />
                                </SelectTrigger>

                                <SelectContent>
                                    {(plans ?? []).map(plan => (
                                        <SelectItem key={plan.id} value={plan.id}>
                                            <PlanLabel plan={plan} />
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <FieldError id='customer-plan-error' message={errors.planId} />
                        </div>

                        <div className='grid gap-1.5'>
                            <Label id='customer-country-label' htmlFor='customer-country'>Country</Label>

                            <Select
                                items={countryItems}
                                value={values.country}
                                onValueChange={value => update('country', typeof value === 'string' ? value : '')}
                            >
                                <SelectTrigger
                                    id='customer-country'
                                    ref={element => { fieldRefs.current.country = element }}
                                    aria-labelledby='customer-country-label'
                                    aria-invalid={errors.country ? true : undefined}
                                    aria-describedby={errors.country ? 'customer-country-error' : undefined}
                                    className='h-9 w-full'
                                >
                                    <SelectValue />
                                </SelectTrigger>

                                <SelectContent>
                                    {COUNTRIES.map(country => (
                                        <SelectItem key={country} value={country}>
                                            {country}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <FieldError id='customer-country-error' message={errors.country} />
                        </div>
                    </div>

                    {plansFailed && (
                        <div className='flex items-center justify-between gap-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning'>
                            <span>Plans could not be loaded.</span>

                            <button
                                type='button'
                                onClick={() => void loadPlans()}
                                className='font-semibold underline underline-offset-2'
                            >
                                Try again
                            </button>
                        </div>
                    )}

                    {formError && (
                        <p role='alert' className='rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger'>
                            {formError}
                        </p>
                    )}

                    <DialogFooter className='-mx-5 -mb-5 mt-1 rounded-b-xl px-5 py-4'>
                        <DialogClose render={<Button type='button' variant='outline' className='h-9 px-3' />}>
                            Cancel
                        </DialogClose>

                        <Button type='submit' disabled={submitting || !plans} className='h-9 px-3'>
                            {submitting && <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />}
                            {submitting ? 'Adding customer' : 'Add customer'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
